import React, { useEffect, useMemo } from "react";
import { MainFlowParamList } from "./types";
import {
  AccountComponent,
  AccountIcon,
  AccountStack,
  DashboardComponent,
  EventsIcon,
  fontFamily,
  HomeIcon,
  MyEventsComponent,
  ScheduleComponent,
  ScheduleIcon,
} from "./MainFlowComponents";
import { useAppSelector } from "hooks/useAppSelector";
import toast from "utils/toast";
import PaymentMethod from "screens/PaymentMethod/PaymentMethod";
import useInitTenantSetting from "hooks/useInitTenantSetting";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { ActivityIndicator, View } from "react-native";
import { useAccount } from "hooks/useAccount";
import i18n from "plugins/localization";
import useCurrentUser from "hooks/useCurrentUser";
import useIsSSO from "hooks/useIsSSO";
import { createDrawerNavigator } from "@react-navigation/drawer";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";

const MainFlow = () => {
  const { account, fetchAccount } = useAccount();
  const accountType = account?.type;

  // Show full account access if account type if full or account has some active subscriptions
  const isFullAccount =
    accountType === "FULL" ||
    account?.subscriptions.some(
      (sub) => filterIneligibleSubscriptions(sub) && sub.status === "ACTIVE"
    );
  const { tenantThemeColor } = useAppSelector((state) => state.tenant);
  const { currentUser, fetchCurrentUser } = useCurrentUser();
  const { authMethod, isSSO } = useIsSSO();

  const Navigator = isSSO
    ? createDrawerNavigator<MainFlowParamList>()
    : createBottomTabNavigator<MainFlowParamList>();

  useInitTenantSetting();

  const accountStatus = useMemo(() => {
    return account?.status;
  }, [account]);

  useEffect(() => {
    fetchAccount();
  }, []);

  // Edge case, when app is running inside Ballpark app's webview
  // the auth store hasn't been populated, so current user must be undefined
  // need to manually fetch user again
  useEffect(() => {
    if (!currentUser || authMethod === "ballpark") {
      fetchCurrentUser();
    }
  }, [currentUser, authMethod]);

  if (!account) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator />
      </View>
    );
  }

  // https://github.com/FanRally/fanrally-core/issues/40
  // if account status is one of DUNNING or SUSPENDED, only show screen
  // of update payment method
  if (accountStatus === "DUNNING" || accountStatus === "SUSPENDED") {
    toast.info("Please update your billing information");
    return (
      <AccountStack.Navigator>
        <AccountStack.Screen
          name="PaymentMethod"
          component={PaymentMethod}
          options={{ title: "Payment method", headerShown: false }}
        ></AccountStack.Screen>
      </AccountStack.Navigator>
    );
  }

  const removeNestedScreenOnBlur = (navigation: any) => () =>
    navigation.setParams({ screen: undefined });
  return (
    <Navigator.Navigator
      screenOptions={{
        drawerPosition: "right",
        headerShown: false,
        tabBarActiveTintColor: tenantThemeColor,
        tabBarStyle: {
          backgroundColor: "white",
        },
      }}
    >
      {isFullAccount && (
        <Navigator.Screen
          name="Home"
          component={DashboardComponent}
          options={{
            tabBarIcon: HomeIcon,
            tabBarLabelStyle: {
              fontSize: 12,
              fontFamily,
            },
            tabBarLabel: i18n.t("labels.home"),
          }}
        />
      )}

      <Navigator.Screen
        name="MyEvents"
        component={MyEventsComponent}
        options={{
          tabBarIcon: EventsIcon,
          tabBarLabel: i18n.t("labels.myGames"),
          tabBarLabelStyle: {
            fontSize: 12,
            fontFamily,
          },
          unmountOnBlur: true,
        }}
        listeners={({ navigation }) => ({
          blur: removeNestedScreenOnBlur(navigation),
        })}
      />

      {isFullAccount && (
        <Navigator.Screen
          name="Schedule"
          component={ScheduleComponent}
          options={{
            tabBarIcon: ScheduleIcon,
            tabBarLabelStyle: {
              fontSize: 12,
              fontFamily,
            },
            unmountOnBlur: true,
            tabBarLabel: i18n.t("labels.schedule"),
          }}
          listeners={({ navigation }) => ({
            blur: removeNestedScreenOnBlur(navigation),
          })}
        />
      )}
      <Navigator.Screen
        name="Account"
        component={AccountComponent}
        options={{
          tabBarIcon: AccountIcon,
          tabBarLabelStyle: {
            fontSize: 12,
            fontFamily,
          },
          unmountOnBlur: true,
          tabBarLabel: i18n.t("labels.account"),
        }}
        listeners={({ navigation }) => ({
          blur: removeNestedScreenOnBlur(navigation),
        })}
      />
    </Navigator.Navigator>
  );
};

export default MainFlow;
